<script module lang="ts">
  import type { Snippet } from 'svelte';
  import type { IconProps } from '../icons/icons.types';
  import type { DropdownPoints } from './dropdown.types';

  export const allowedTags = ['ul', 'div'] as const;
  export type AllowedTag = (typeof allowedTags)[number];

  export const allowedAligns = ['right', 'left'] as const;
  export type AllowedAlign = (typeof allowedAligns)[number];

  export interface Props {
    /** The trigger, unless `trigger` draws its own. */
    icon?: IconProps['icon'];
    label: string;
    tag?: AllowedTag;
    align?: AllowedAlign;
    points?: DropdownPoints;
    /** A trigger of its own (a labelled button); it gets the function that opens and closes. */
    trigger?: Snippet<[toggle: () => void]>;
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { setContext } from 'svelte';
  import { slide } from 'svelte/transition';
  import { linear } from 'svelte/easing';
  import IconComponent from '../icons/Icon.svelte';
  import DropdownPointsList from './DropdownPointsList.svelte';
  import { CLOSE_CONTEXT_KEY, type CloseFn } from './dropdown.types';

  const { icon, label, tag, align, points, trigger, children }: Props = $props();

  // -
  const DefaultTag = allowedTags[0];
  const ElementTag = $derived(tag && allowedTags.includes(tag) ? tag : DefaultTag);

  // -
  const DefaultAlign = allowedAligns[0];
  const Align = $derived(align && allowedAligns.includes(align) ? align : DefaultAlign);

  // -
  let Container: HTMLElement | undefined = $state();
  let IsOpen = $state(false);

  function toggleOpen(): void {
    IsOpen = !IsOpen;
  }

  function closePanel(): void {
    IsOpen = false;
  }

  function closeOnOutside(event: PointerEvent): void {
    const Target = event.target;

    if (!IsOpen || (Target instanceof Node && Container?.contains(Target) === true)) {
      return;
    }

    closePanel();
  }

  function closeOnEscape(event: KeyboardEvent): void {
    if (!IsOpen || event.key !== 'Escape') {
      return;
    }

    closePanel();
  }

  setContext<CloseFn>(CLOSE_CONTEXT_KEY, closePanel);
</script>

<svelte:window onpointerdown={closeOnOutside} onkeydown={closeOnEscape} />

<div class="dropdown" bind:this={Container}>
  {#if trigger}
    {@render trigger(toggleOpen)}
  {:else if icon}
    <button type="button" class="dropdownTrigger" aria-label={label} aria-expanded={IsOpen} onclick={toggleOpen}>
      <IconComponent {icon} size={1.5} />
    </button>
  {/if}
  {#if IsOpen}
    <div class="dropdownPanel lyBorder lyRounded lyShadow {Align === 'left' ? 'alignLeft' : ''}" transition:slide={{ axis: 'y', easing: linear, duration: 150 }}>
      <svelte:element this={ElementTag} class="dropdownList">
        {#if points}
          <DropdownPointsList {points} />
        {/if}
        {@render children?.()}
      </svelte:element>
    </div>
  {/if}
</div>

<style lang="scss">
  @use '../../../styles/display';

  .dropdown {
    --dropdown-inset: calc(var(--inset-abs) / 2);
    --dropdown-border-radius: calc(var(--border-radius) / 2);

    position: relative;
    display: inline-block;

    .dropdownTrigger {
      @include display.inline-block;
      line-height: 1;
      cursor: pointer;
    }

    .dropdownPanel {
      position: absolute;
      top: calc(100% + var(--outset-abs-half));
      right: 0;
      z-index: 50;
      width: var(--dropdown-panel-width, auto);
      min-width: 12rem;
      padding: var(--dropdown-inset);
      background: var(--layout-background);

      &.alignLeft {
        right: auto;
        left: 0;
      }

      .dropdownList {
        @include display.block;
        list-style: none;
        line-height: 1;
        white-space: nowrap;
        cursor: default;
        font-size: var(--dropdown-font-size, 1em);
      }
    }
  }
</style>
